// The mate bar's pure half — everything the Alarms page's mate needs to decide
// WITHOUT a DOM (cloud/web's vitest runs `src/**/*.test.ts` under Node).
//
// The mate is the existing Neo machinery (ADR 0161) wearing the Alarms page's
// chrome: one turn at a time, streamed from `POST /v1/neo/chat/stream`, its
// `alarm_proposal` frames decoded by `lib/neoAlarmProposal`. Nothing here talks
// to the network — components/alarms/redesign/MateBar.tsx owns the fetch and the
// React state, and folds each SSE frame through `foldMateEvent`.
//
// Two rules shape the folding, and both come from ADR 0161:
//
//  1. A turn can FAIL and still have produced real drafts. The server emits
//     `alarm_proposal` on the error path deliberately, precisely so a dropped
//     connection does not rob the owner of an accepted draft. So an error never
//     discards proposals, and the honest "the mate couldn't answer" line is
//     suppressed when cards did arrive — the mate did answer, in cards.
//  2. The server always ends a turn with a terminal `done` (or `error`) frame.
//     A clean close WITHOUT one means the connection dropped mid-answer; that is
//     a failure, not a silent success (`endMateTurn`).

import { stripNeoChartMarkers } from '@neomaris/protocol';
import { foldNeoAgentEvent, neoAgentBusyLine, type NeoAgentAtWork } from './neoAgentProgress';
import { parseAlarmProposals, type NeoAlarmProposal } from './neoAlarmProposal';
import type { NeoStreamEvent } from './neoStream';

/** One in-flight (or finished) mate turn. The bar shows the LATEST turn only —
 * this is a command bar, not a chat history. */
export interface MateTurnState {
  /** The assistant's running text reply for this turn. */
  readonly text: string;
  /** Drafted alarms reported this turn. Nothing exists until the owner clicks.
   * Shrinks as cards are confirmed or discarded. */
  readonly proposals: readonly NeoAlarmProposal[];
  /** Whether this turn EVER produced a draft. Sticky, unlike `proposals`: the
   * "couldn't answer" line stays suppressed after the owner has dealt with the
   * cards, because the mate did answer — accusing it afterwards would be false. */
  readonly drafted: boolean;
  /** Whether a terminal `done` frame arrived. */
  readonly sawDone: boolean;
  /** Whether the turn failed (an `error` frame, or no terminal frame at all). */
  readonly errored: boolean;
  /** The specialists Neo is consulting right now, from `agent` frames (TOM-543).
   * Emptied when the turn ends. */
  readonly agentsAtWork: readonly NeoAgentAtWork[];
  /**
   * The server's own words when it refused the turn with `429 demo_limit`: a
   * Try it guest past a cap (TOM-868). The bar shows them in place of the
   * failure line, since they say what happened and point to the beta.
   */
  readonly limitMessage?: string;
}

/** A fresh turn: nothing said, nothing drafted. */
export function startMateTurn(): MateTurnState {
  return {
    text: '',
    proposals: [],
    drafted: false,
    sawDone: false,
    errored: false,
    agentsAtWork: [],
  };
}

/** A turn that never got off the ground (the request threw, or the route
 * answered with a non-OK status before any frame). */
export function failedMateTurn(): MateTurnState {
  return { ...startMateTurn(), errored: true };
}

/**
 * Fold one decoded SSE frame into the turn. Unknown event names are ignored:
 * the stream carries frames this bar has no use for (`mutations`), and new ones
 * may appear before this file learns about them.
 *
 * `pageBoatId` is the mate bar's page scope: proposals stamped with any other
 * boat are dropped BEFORE they can set `drafted`, so an all-foreign frame
 * leaves the turn exactly as it was (see `parseAlarmProposals` on why).
 */
export function foldMateEvent(
  state: MateTurnState,
  event: string,
  data: NeoStreamEvent,
  pageBoatId?: string
): MateTurnState {
  switch (event) {
    case 'delta':
      return { ...state, text: state.text + (data.text ?? '') };
    // A tool round: the server withdraws the partial narration it had started.
    // Text goes, drafts STAY — they were reported, not narrated.
    case 'reset':
      return { ...state, text: '' };
    case 'replace':
      return { ...state, text: data.content ?? '' };
    case 'alarm_proposal': {
      const arrived = parseAlarmProposals(data.proposals, pageBoatId);
      if (arrived.length === 0) return state;
      return { ...state, proposals: [...state.proposals, ...arrived], drafted: true };
    }
    // A specialist Neo consults started or ended (TOM-543): the busy label
    // names what it checks, never a value.
    case 'agent': {
      const agentsAtWork = foldNeoAgentEvent(state.agentsAtWork, data);
      return agentsAtWork === state.agentsAtWork ? state : { ...state, agentsAtWork };
    }
    // A terminal frame ends every consult with the turn.
    case 'error':
      return { ...state, errored: true, agentsAtWork: [] };
    case 'done':
      return { ...state, sawDone: true, agentsAtWork: [] };
    default:
      return state;
  }
}

/**
 * Drop one proposal by its stable identity — a confirm completed, or the owner
 * discarded the card. Identity, never position: with positional removal, a
 * discard above an in-flight confirm renumbers the survivors, and the
 * confirm's completion then removes whichever card INHERITED its index —
 * an unanswered draft vanishes while the created one stays on screen.
 * An unknown id is a no-op (the card was already dealt with).
 */
export function removeMateProposal(state: MateTurnState, clientId: string): MateTurnState {
  const proposals = state.proposals.filter((p) => p.clientId !== clientId);
  return proposals.length === state.proposals.length ? state : { ...state, proposals };
}

/**
 * Close the turn. A stream that ended without a terminal frame dropped
 * mid-answer, which is a failure — the bar must not present a truncated reply
 * as a finished one.
 */
export function endMateTurn(state: MateTurnState): MateTurnState {
  // However it ended, no specialist is still at work (TOM-543).
  const ended = state.agentsAtWork.length === 0 ? state : { ...state, agentsAtWork: [] };
  if (ended.sawDone || ended.errored) return ended;
  return { ...ended, errored: true };
}

/** The one honest failure line. Deliberately fixed wording: the provider's own
 * message is not the owner's business, and a fabricated draft is never shown. */
export const MATE_ERROR_LINE = 'The mate couldn’t answer right now — try again in a moment.';

/**
 * Whether to show that line. A failed turn that still produced drafts shows the
 * cards instead: they are valid, creatable, and saying "couldn't answer" over
 * them would be false (ADR 0161's error-path proposals).
 */
export function mateErrorLine(state: MateTurnState): string | null {
  if (!state.errored) return null;
  if (state.drafted) return null;
  return state.limitMessage ?? MATE_ERROR_LINE;
}

/** The compact in-flight label: thinking until something arrives, drafting once
 * the turn has started producing, and what Neo is checking while a specialist
 * works (TOM-543), in the bar's own name for it. */
export function mateBusyLabel(state: MateTurnState): string {
  const checking = neoAgentBusyLine(state.agentsAtWork, 'The mate');
  if (checking !== null) return checking;
  if (state.proposals.length > 0) return 'The mate is drafting an alarm…';
  if (state.text.trim().length > 0) return 'The mate is drafting…';
  return 'The mate is thinking…';
}

/** Every newline and run of spaces as ONE space. The bar has one line to spend,
 * so whatever it shows arrives flattened. */
function oneLine(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** A table cell of nothing but dashes and alignment colons — GFM's header rule
 * (`|---|:--:|`). It carries no words, so it is dropped, not flattened. */
const TABLE_RULE_CELL = /^:?-+:?$/;

/** An ATX heading line: up to three spaces, one to six `#` and a space (or the
 * line's end), its words, and an optional closing run of `#`. `#2` is not one. */
const HEADING_LINE = /^ {0,3}#{1,6}(?:[ \t]+|$)(.*?)(?:[ \t]+#+)?[ \t]*$/;

/** Ends a sentence already, so a flattened heading needs no full stop. */
const SENTENCE_END = /[.!?:…]$/;

/**
 * Every heading line as the words it holds (TOM-697). Neo opens a finding with
 * its conclusion as a `## ` line and a report's sections under `### `; this
 * line draws no heading, so the markers go. A heading the reply has gone past
 * ends as a sentence, so the conclusion does not run into the sentence after
 * it once the line is flattened. The last line may still be streaming, so it
 * gets no full stop.
 */
function stripHeadings(text: string): string {
  const lines = text.split('\n');
  return lines
    .map((line, i) => {
      const heading = HEADING_LINE.exec(line);
      if (heading === null) return line;
      const words = (heading[1] ?? '').trim();
      const whole = i < lines.length - 1;
      return whole && words.length > 0 && !SENTENCE_END.test(words) ? `${words}.` : words;
    })
    .join('\n');
}

/**
 * Markdown to plain text, for a surface that cannot render it.
 *
 * The mate's turns come back as Markdown — bold, GFM tables, links (the Neo
 * system prompt asks for linked manual citations), the same wire NeoChat feeds
 * to react-markdown. NeoChat renders it; this one line cannot, so raw syntax
 * reaches the owner as noise: a serialized table (`| Setting | Value | |---|---|
 * …`) is unreadable, and `**bold**` is just wrong. Stripping is not a rewrite of
 * what the mate said — the words all survive, and the untouched text still rides
 * the `title` attribute.
 *
 * Deliberately narrow: it removes markers, never words. Underscore emphasis is
 * taken only at a word boundary so a `fuel_level_1` signal path keeps its
 * underscores, and backticks are simply deleted rather than matched in pairs,
 * because a streaming reply is routinely cut mid-span. A heading's `#` markers
 * go too, and a finished heading ends with a full stop (stripHeadings).
 */
function stripMarkdown(text: string): string {
  // A chart cannot ride this one line: its marker goes, the words stay (ADR 0230).
  const inline = stripHeadings(stripNeoChartMarkers(text))
    // [text](url) and ![alt](url) — the words stay, the URL is not clickable here.
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/`+/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(?<![A-Za-z0-9])__(.+?)__(?![A-Za-z0-9])/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/(?<![A-Za-z0-9])_(.+?)_(?![A-Za-z0-9])/g, '$1');

  if (!inline.includes('|')) return inline;
  // A table flattens to its cells, in reading order, separated by a middle dot:
  // the rows and the column alignment are what the line cannot carry, the cells
  // are the content. Everything before the first pipe is prose — it introduces
  // the table ("Here's what it will do:"), so it keeps its sentence spacing.
  const [prose = '', ...cells] = inline.split('|');
  const fragments = cells
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0 && !TABLE_RULE_CELL.test(cell));
  return fragments.length === 0 ? prose : `${prose} ${fragments.join(' · ')}`;
}

/**
 * The assistant's reply as ONE plain-text line. The bar shows the latest turn's
 * text under the input as a single muted line (the full, unstripped text rides
 * the `title` attribute).
 */
export function mateReplyLine(text: string): string {
  return oneLine(stripMarkdown(text));
}

/**
 * The reply line to render, or '' for none.
 *
 * A turn that drafted shows NO line: its text is narration OF the cards ("I've
 * drafted a Starboard alternator low voltage alarm for your confirmation, here's
 * what it will do…"), and the card below states the same thing in the form the
 * owner acts on. Two tellings of one answer, the worse one first.
 *
 * Sticky on `drafted` rather than on the live card count, for the same reason
 * `mateErrorLine` is: once the owner has confirmed or discarded, the cards go,
 * and re-posting "drafted for your confirmation" under the saved receipt would
 * be stale narration of something already dealt with.
 */
export function mateReply(state: MateTurnState): string {
  if (state.drafted) return '';
  return mateReplyLine(state.text);
}

/** The proposal card's quoted transcript line — what the owner asked, and the
 * flat statement that nothing has been saved. Quoted verbatim apart from the
 * flattening: the markdown strip above is for the mate's prose, and applying it
 * here would put words in the owner's mouth. */
export function mateTranscriptLine(command: string): string {
  return `“${oneLine(command)}” — drafted, nothing saved yet`;
}

/** "Armed in" for the card, or null when the proposal names no modes. */
export function mateArmedLine(enabledModes: readonly string[]): string | null {
  return enabledModes.length === 0 ? null : `Armed in ${enabledModes.join(', ')}`;
}

/** The receipt bar's line, and whether it is a plain success or one that needs
 * the owner's attention. */
export interface MateReceipt {
  readonly message: string;
  readonly needsAttention: boolean;
}

/**
 * What the teal receipt bar says after a confirmed create.
 *
 * The design's wording is "<title> saved — applied when the boat next connects",
 * and for an ordinary create that is exactly right. It is NOT right for
 * `compileStatus: 'pending'` or `'disabled'`: those are successes (the alarm is
 * saved) that are not on their way to being watched, and promising the boat will
 * apply them would be the same overclaim `proposalCreatedMessage` exists to
 * avoid. Those two get their own honest ending and the attention tone.
 */
export function mateReceiptMessage(title: string, compileStatus: string | null): MateReceipt {
  if (compileStatus === 'pending') {
    return {
      message: `${title} saved — but it isn’t watching a signal yet.`,
      needsAttention: true,
    };
  }
  if (compileStatus === 'disabled') {
    return {
      message: `${title} saved — but it is disabled, so it isn’t evaluating.`,
      needsAttention: true,
    };
  }
  return {
    message: `${title} saved — applied when the boat next connects.`,
    needsAttention: false,
  };
}
